/* eslint-disable react-hooks/exhaustive-deps */
import { Search, Pencil, Plus, RotateCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Toggle from 'react-toggle'
import 'react-toggle/style.css'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddRack from '../../../components/modal/racks/ModalAddRack'
import ModalUpdateRack from '../../../components/modal/racks/ModalUpdateRack'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import { firstLetterUpperCase } from '../../../utils/TextFormatter'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const MasterRackPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [warehouses, setWarehouses] = useState([])
  const [racks, setRacks] = useState([])
  const [editId, setEditId] = useState(0)
  const [filter, setFilter] = useState({
    id: state?.id !== undefined ? state.id : state?.warehouse_id || '',
    q: state?.q || ''
  })

  const [debouncedQ, setDebouncedQ] = useState(filter.q)

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const { per_page, current_page } = meta

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(filter.q)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [filter.q])

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await api.get(
        'master/warehouses/all?is_parent=true&forUser=1'
      )
      setWarehouses(res.data.data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }, [])

  const fetchRacks = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = {
        warehouse_id: filter.id,
        per_page,
        page: current_page,
        q: debouncedQ
      }
      const res = await api.get('master/racks', { params })
      const { metadata, data } = res.data
      setRacks(data)
      setMeta(metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoading(false)
    }
  }, [per_page, current_page, filter.id, debouncedQ])

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleEdit = (id) => {
    setShowModalUpdate(true)
    setEditId(id)
  }

  const handleToggle = async (id, status, rack) => {
    try {
      await api.patch(`master/racks/${id}`, {
        ...rack,
        is_active: !status
      })
      fetchRacks()
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  useEffect(() => {
    fetchWarehouses()
  }, [])

  useEffect(() => {
    fetchRacks()
  }, [fetchRacks])

  const handleFilterChange = (name, value) => {
    setFilter((prev) => ({
      ...prev,
      [name]: value
    }))
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleReset = () => {
    setFilter({
      id: '',
      q: ''
    })
    setDebouncedQ('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const warehouseOptions = [
    { value: '', label: 'All Warehouses' },
    ...warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const getSelectedWarehouse = () => {
    return warehouses.find((w) => w.id === filter.id)
  }

  const disabledAdd = filter.id === ''

  return (
    <div className="container-fluid px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Rack</h3>
        </Col>
        <Col className="text-end">
          <Button
            className="btn btn-primary-orange rounded px-4 py-2 fw-bold me-2"
            onClick={() => setShowModalAdd(true)}
            disabled={disabledAdd}
            style={{ fontSize: '0.85rem' }}
          >
            <Plus size={16} className="me-2" />
            Add Rack
          </Button>
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 py-2 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search Rack</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Rack by name..."
                  value={filter.q}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, q: e.target.value }))
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                name="warehouse"
                id="warehouse"
                options={warehouseOptions}
                value={warehouseOptions.find((o) => o.value === filter.id)}
                onChange={(e) => handleFilterChange('id', e?.value || '')}
                styles={SelectStyles}
                placeholder="All Warehouses"
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col xs={12}>
          <Table
            responsive
            hover
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Name
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Type
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Area
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Total Beam
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Total Level
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Slot/ Beam
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Status
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              )}
              {!isLoading && racks.length === 0 && <RowEmpty colSpan={8} />}
              {!isLoading &&
                racks.length > 0 &&
                racks.map((rack) => (
                  <tr
                    key={rack.id}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td className="p-2 align-middle fw-semibold text-primary">
                      {rack.name}
                    </td>
                    <td className="p-2 align-middle">
                      {firstLetterUpperCase(rack.type)}
                    </td>
                    <td className="p-2 align-middle">{rack?.area?.name}</td>
                    <td className="p-2 align-middle">{rack.total_beam}</td>
                    <td className="p-2 align-middle">{rack.total_level}</td>
                    <td className="p-2 align-middle">{rack.total_slot}</td>
                    <td className="p-2 align-middle">
                      <Toggle
                        className="actived"
                        defaultChecked={rack.is_active}
                        onClick={() =>
                          handleToggle(rack.id, rack.is_active, rack)
                        }
                        icons={false}
                      />
                    </td>
                    <td className="p-2 align-middle text-end">
                      <TableAction>
                        <Button
                          variant="light"
                          className="text-secondary border rounded shadow-sm p-0 me-2"
                          style={{ width: '35px', height: '35px' }}
                          onClick={() =>
                            navigate(`/setting/racks/detail/${rack.id}`, {
                              state: {
                                id: filter.id,
                                warehouse_id: rack?.warehouse?.warehouse_id,
                                q: filter.q,
                                rackName: rack.name,
                                warehouseName: rack?.warehouse?.warehouse_name,
                                type: rack?.type
                              }
                            })
                          }
                        >
                          <Search size={16} />
                        </Button>
                        <Button
                          variant="light"
                          className="text-secondary border rounded shadow-sm p-0"
                          style={{ width: '35px', height: '35px' }}
                          onClick={() => handleEdit(rack.id)}
                        >
                          <Pencil size={13} />
                        </Button>
                      </TableAction>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>

      <ModalAddRack
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        warehouseId={{
          id: filter.id,
          warehouseId: getSelectedWarehouse()?.warehouse_id
        }}
        refreshData={fetchRacks}
      />

      <ModalUpdateRack
        show={showModalUpdate}
        onHide={() => setShowModalUpdate(false)}
        id={editId}
        refreshData={fetchRacks}
        warehouseId={{
          id: filter.id,
          warehouseId: getSelectedWarehouse()?.warehouse_id
        }}
      />
    </div>
  )
}

export default MasterRackPage
